<template>
  <div>
    <base-loading :visible="isLoading" />
    <file-viewer v-model="fileModal" :files="files" :title="$t('files')" />
    <upload-request-files :clients="clients" :document="document" v-model="uploadFileModal"
      :title="$t('files') | capitalize" @submit="uploadFiles" />
    <add-document-request :clients="clients" :document="document" v-model="addRequestModal"
      :title="$t('request') | capitalize" @submit="createRequest" />
    <confirmation-modal v-model="confirmation" :item="item" @accept="confirmUpdate"
      :title="$t('confirm') + ' - ' + $t(item.action)" :message="$t('confirm_action')" />
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-full md:col-span-2 md:col-start-1  xs:col-start-1">
        <vs-button color="success" icon="note_add" @click="addRequestModal = true"> Solicitar
        </vs-button>
      </div>
      <div class="col-span-full md:col-span-2 md:col-start-11  xs:col-start-1">
        <search-input v-model="search" @search="onSearch" @clear="onSearch" />
      </div>
      <div class="col-span-full col-start-1">
        <document-requests-table :items="items" @loadMore="loadMore" @sortChanged="onSortChanged"
          @updateRequest="onUpdateRequest" @file="onFile" @upload="onUpload" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useDocumentRequest } from '../composables/use-document-request'
import { useNotifier } from '@/composables/useNotifier'
import { useNavigator } from '@/composables/useNavigator'
import FileViewer from '@/views/app/components/FileViewer'
import DocumentRequestsTable from '../components/DocumentRequestsTable'
import UploadRequestFiles from '../components/UploadRequestFiles'
import AddDocumentRequest from '../components/AddDocumentRequest'
import ConfirmationModal from '@/views/app/components/ConfirmationModal'
import BaseLoading from '@/views/app/components/BaseLoading'
import SearchInput from '@/views/app/components/SearchInput'
import { BASE_URL } from '@/urls.js'

const { notify } = useNotifier()
const { goTo, getParam } = useNavigator()
const search = ref('')
const documentRequest = useDocumentRequest()
const sort = ref({})
const item = ref({})
const documentId = getParam('document')
const items = computed(() => documentRequest.state.items)
const currentPage = computed(() => documentRequest.state.currentPage)
const lastPage = computed(() => documentRequest.state.lastPage)
const isLoading = computed(() => documentRequest.state.loading)
const document = ref({})
const clients = ref([])
const confirmation = ref(false)
const addRequestModal = ref(false)
const uploadFileModal = ref(false)
const files = ref([])
const fileModal = ref(false)

const loadData = async (params = {}) => {
  const payload = {
    documentId: documentId.value,
    page: documentRequest.state.currentPage || 1,
    search: search.value,
    column: sort.value.column,
    order: sort.value.order,
    ...params
  }
  const result = await documentRequest.getDocumentRequest(payload)
  if (!result.success) {
    notify({ title: 'error', text: 'message_error', color: 'danger' })
    return
  }
}

const getDocument = async (params = {}) => {
  const payload = {
    documentId: documentId.value,
  }
  const result = await documentRequest.getDocument(payload)
  if (!result.success) {
    notify({ title: 'error', text: 'message_error', color: 'danger' })
    return
  }
  document.value = {
    name: result.response.document.documentName,
    id: result.response.document.documentId,
    frequencyId: result.response.document.frequencyId,
    frequency: result.response.document.frequencyName,
  }
}

const loadMore = async () => {
  if (currentPage.value >= lastPage.value) return
  const newPage = documentRequest.state.currentPage + 1
  await loadData({ page: newPage })
}

const onSearch = async () => {
  await loadData({ page: 1, search: search.value })
}

const onSortChanged = async (sortData) => {
  sort.value.column = sortData.key
  sort.value.order = sortData.order
  await loadData({ column: sort.value.column, order: sort.value.order, page: 1 })
}

const getClients = async () => {
  const payload = { id: 1 }
  const result = await documentRequest.getClients(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  clients.value = [...result.response.clients]
}

const createRequest = async (data) => {

  const payload = {
    document: documentId.value,
    endDate: data.endDate,
    startDate: data.startDate,
    client: data.client === null ? null : data.client.ClientId,
    auto: data.auto
  }

  if (data.auto) {
    addRequestAuto(payload)
    return;
  }
  addRequest(payload)

}

const addRequest = async (payload) => {
  const result = await documentRequest.addRequest(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  addRequestModal.value = false
  notify({ title: 'success', text: 'message_save', color: 'success' })

}


const addRequestAuto = async (payload) => {
  const result = await documentRequest.addRequestAuto(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  addRequestModal.value = false
  notify({ title: 'success', text: 'message_save', color: 'success' })
  loadData()
}


const onUpdateRequest = (data) => {
  confirmation.value = true
  item.value = { ...data }
}

const confirmUpdate = (data) => {
  confirmation.value = false
  if (data.action === 'delete') {
    deleteDocumentRequest(data)
  } else {
    updateDocumentRequest(data)
  }
}

const onUpload = (data) => {
  uploadFileModal.value = true
  item.value = { ...data }

}

const deleteDocumentRequest = async (data) => {
  const payload = { id: data.id }
  const result = await documentRequest.deleteDocumentRequest(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  notify({ title: 'success', text: 'message_delete', color: 'success' })
}

const updateDocumentRequest = async (data) => {
  const payload = { id: data.id, action: data.action }
  const result = await documentRequest.updateDocumentRequest(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  notify({ title: 'success', text: 'message_update', color: 'success' })
}

const uploadFiles = async (inputFiles) => {
  const files = [...inputFiles]

  const formData = new FormData()
  formData.append('id', item.value.id)
  files.forEach(function (file, i) {
    formData.append('files[]', file)
  })
  const result = await documentRequest.uploadFiles(formData)

  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
   uploadFileModal.value = false
  notify({ title: 'success', text: 'message_update', color: 'success' })
}


const onFile = async (row) => {
  const payload = { requestId: row }
  const result = await documentRequest.getFiles(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  if (result.success && result.response && Array.isArray(result.response.file)) {
    files.value = result.response.file.map((file, index) => {
      return {
        ...file,
        src: BASE_URL + 'storage/' + file.src 
      }
    })
    fileModal.value = true;
  } else {
    files.value = [];
  }
}


onMounted(() => {
  loadData()
  getDocument()
  getClients()
})

</script>
